import { Box, Typography, TextField, Button, CircularProgress, Paper } from "@mui/material";
import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../services/authService";

const EditStores = ({ data, fetch, onClose }) => {
    const [formData, setFormData] = useState({
        name: "",
        address: "",
        phone: "",
        email: ""
    });
    const [errors, setErrors] = useState({
        name: "",
        address: "",
        phone: "",
        email: ""
    });
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // console.log(data);
        if (data) {
            setFormData({
                name: data.name || "",
                address: data.address || "",
                phone: data.phone || "",
                email: data.email || ""
            });
        }
    }, [data]);

    const validateName = (name) => {
        if (!name.trim()) return "Store name is required";
        if (name.length < 3) return "Store name must be at least 3 characters long";
        if (name.length > 50) return "Store name must be less than 50 characters";
        return "";
    };

    const validateAddress = (address) => {
        if (!address.trim()) return "Address is required";
        if (address.length < 10) return "Please enter complete address";
        if (address.length > 200) return "Address is too long";
        return "";
    };

    const validatePhone = (phone) => {
        if (!phone) return "Phone number is required";
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phoneRegex.test(phone)) return "Please enter a valid 10-digit phone number";
        return "";
    };

    const validateEmail = (email) => {
        if (!email) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) return "Please enter a valid email address";
        return "";
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Validate field
        let errorMessage = "";
        switch (field) {
            case "name":
                errorMessage = validateName(value);
                break;
            case "address":
                errorMessage = validateAddress(value);
                break;
            case "phone":
                errorMessage = validatePhone(value);
                break;
            case "email":
                errorMessage = validateEmail(value);
                break;
            default:
                break;
        }

        setErrors(prev => ({
            ...prev,
            [field]: errorMessage
        }));
    };

    const validateForm = () => {
        const newErrors = {
            name: validateName(formData.name),
            address: validateAddress(formData.address),
            phone: validatePhone(formData.phone),
            email: validateEmail(formData.email)
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== "");
    };

    const onUpdateStore = async () => {
        if (!validateForm()) {
            setErrorMsg("Please fix all errors before submitting");
            return;
        }

        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        try {
            // Get token from localStorage
            const userData = localStorage.getItem('invCreator');
            if (!userData) {
                throw new Error('Authentication token not found');
            }
            const { token } = JSON.parse(userData);

            // Create payload with only changed fields
            const payload = {};
            if (formData.name !== data.name) payload.name = formData.name;
            if (formData.address !== data.address) payload.address = formData.address;
            if (formData.phone !== data.phone) payload.phone = formData.phone;
            if (formData.email !== data.email) payload.email = formData.email;

            // If no fields were changed, show message and return
            if (Object.keys(payload).length === 0) {
                setSuccessMsg("No changes were made");
                setLoading(false);
                return;
            }

            // Make API call with token
            const response = await axios.put(
                `${API_URL}/update_store_by_id/${data.store_id}`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            // console.log(response);

            if (response.statusText === "OK") {
                setSuccessMsg(response.data.message);
                onClose();
                fetch();
            } else {
                setErrorMsg(response.data.message || "Failed to update store");
            }
        } catch (error) {
            console.error("Error updating store:", error);
            setErrorMsg(error.response?.data?.message || "An error occurred while updating the store");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <TextField
                fullWidth
                variant="outlined"
                label="Store Name"
                value={formData.name}
                onChange={handleChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                sx={{ mb: 2 }}
                inputProps={{
                    maxLength: 50
                }}
            />

            <TextField
                fullWidth
                variant="outlined"
                label="Address"
                value={formData.address}
                onChange={handleChange('address')}
                error={!!errors.address}
                helperText={errors.address}
                sx={{ mb: 2 }}
                multiline
                rows={3}
                inputProps={{
                    maxLength: 200
                }}
            />

            <TextField
                fullWidth
                variant="outlined"
                label="Phone Number"
                value={formData.phone}
                onChange={handleChange('phone')}
                error={!!errors.phone}
                helperText={errors.phone}
                sx={{ mb: 2 }}
                inputProps={{
                    maxLength: 10
                }}
            />

            <TextField
                fullWidth
                variant="outlined"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                sx={{ mb: 2 }}
            />

            {errorMsg && (
                <Typography color="error" sx={{ textAlign: "center", mb: 2 }}>
                    {errorMsg}
                </Typography>
            )}

            <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={onUpdateStore}
                disabled={loading}
                sx={{ height: 48 }}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Update Store"}
            </Button>

            {successMsg && (
                <Typography color="success.main" sx={{ textAlign: "center", mt: 2 }}>
                    {successMsg}
                </Typography>
            )}
        </Box>
    );
};

export default EditStores;